import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { filter } from 'rxjs/operators';
import { Geolocation } from '@capacitor/geolocation';
import { Device } from '@capacitor/device';
import { CityService } from './city.service';
import { environment } from '../../../environments/environment';

const APP = 'passengers-app';
const ANALYTICS_KEY = (environment as any).analyticsKey ?? 'syncc-analytics';

@Injectable({ providedIn: 'root' })
export class TrackingService {
    private router = inject(Router);
    private http = inject(HttpClient);
    private cityService = inject(CityService);

    private sessionStart = Date.now();
    private pageEnteredAt = 0;
    private currentPath = '';
    private _userId: number | null = null;
    private _tenantId: number | null = null;
    private _model: string | undefined;
    private _osVersion: string | undefined;

    init() {
        this.loadDeviceInfo();
        this.trackSessionStart();

        this.router.events.pipe(
            filter(e => e instanceof NavigationEnd),
        ).subscribe((e: any) => {
            if (this.currentPath && this.pageEnteredAt) {
                this.send('page_view', { path: this.currentPath, durationMs: Date.now() - this.pageEnteredAt });
            }
            this.currentPath = e.urlAfterRedirects ?? e.url;
            this.pageEnteredAt = Date.now();
        });

        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this.send('session_end', { durationMs: Date.now() - this.sessionStart });
            });
        }
    }

    private async loadDeviceInfo() {
        try {
            const info = await Device.getInfo();
            this._model = info.model ?? undefined;
            this._osVersion = info.osVersion ?? undefined;
        } catch {
            // ignore — not available on web
        }
    }

    private async trackSessionStart() {
        let coords: { latitude: number; longitude: number } | undefined;
        try {
            const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 3000 });
            coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        } catch {
            // ignore
        }
        this.send('session_start', coords ? { metadata: coords } : {});
    }

    setUser(userId: number, tenantId: number) {
        this._userId = userId;
        this._tenantId = tenantId;
    }

    trackError(errorCode: number, errorPath: string, method: string) {
        this.send('api_error', { errorCode, errorPath, metadata: { method } });
    }

    private send(eventType: string, extra: Record<string, any>) {
        const city = this.cityService.active?.()?.id ?? 'unknown';
        const apiBase = this.cityService.apiBaseUrl();
        this.http.post(
            `${apiBase}/analytics/events`,
            {
                app: APP,
                eventType,
                city,
                userId: this._userId ?? undefined,
                tenantId: this._tenantId ?? undefined,
                deviceType: getDeviceType(),
                platform: getPlatform(),
                model: this._model,
                osVersion: this._osVersion,
                ...extra,
            },
            { headers: { 'x-analytics-key': ANALYTICS_KEY } },
        ).subscribe({ error: () => {} });
    }
}

function getDeviceType(): string {
    if (typeof navigator === 'undefined') return 'mobile';
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|mini|windows\sce|palm/i.test(ua)) return 'mobile';
    return 'desktop';
}

function getPlatform(): string {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    return 'web';
}
